import apiClient from '../services/api';
import type { Paginated } from '../types';

// The delivery agent's web page (/agent) - the same endpoints the mobile
// app uses (app/api/v1/delivery.py, dairy.py, operations.py), nothing new
// on the server. Signs in with the agent's normal staff username/password.

export type Shift = 'Morning' | 'Evening';
export type AccountStatus = 'Ok' | 'LowBalance' | 'WalletEmpty' | 'Due' | 'Overdue';

/** GET /agent/route - one row per shift still to deliver. */
export interface RouteItem {
  customer_id: string;
  customer_name: string;
  sale_date: string;
  shift: Shift;
  litres: string;
  rate_per_litre: string;
  change_note?: string | null;
  billing_mode?: 'Prepaid' | 'Postpaid';
  account_status?: AccountStatus;
  account_warning?: string | null;
  wallet_balance?: string | null;
  overdue_amount?: string | null;
  is_current_shift?: boolean;
  assigned_agent_name?: string | null;
}

export interface Delivery {
  id: string;
  customer_id: string;
  customer_name: string;
  agent_id: string;
  delivery_date: string;
  shift?: Shift | null;
  verification_mode: 'QRScan' | 'OfflineBypass' | 'Skipped';
  captured_at: string;
  distance_from_registered_m?: number | null;
  gps_flag: 'OK' | 'Suspicious' | string;
  reviewed: boolean;
  audit_flagged: boolean;
  litres?: string | null;
  amount?: string | null;
  account_warning?: string | null;
  sale_status?: 'Pending' | 'Approved' | 'Rejected' | null;
}

export interface HubShift {
  shift: Shift;
  is_current_shift: boolean;
  started: boolean;
  litres: string;
  sale_count: number;
  pending_approval: number;
}

export interface AgentOverview {
  user_id: string;
  username: string;
  assigned_customers: number;
  deliveries_pending_now: number;
  deliveries_pending_earlier: number;
  deliveries_done_today: number;
  deliveries_skipped_today: number;
  tasks_open: number;
  tasks_overdue: number;
  tasks_in_progress: number;
  tasks_done_today: number;
  last_activity_at?: string | null;
}

export interface AgentDetail {
  agent: AgentOverview;
  pending: RouteItem[];
  done_today: Delivery[];
  tasks: { assignment_id: string; title: string; task_type: string; priority: string; due_date?: string | null; started_at?: string | null; completed_at?: string | null }[];
  sales_recent?: { id: string; sale_code?: string | null; sale_category: string; party_name: string; shift?: string | null; litres: string; amount?: string | null; status: string; created_at: string }[];
  hub?: HubShift[];
}

/** GET /task-assignments/mine */
export interface MyTask {
  kind?: 'assignment' | 'occurrence';
  assignment_id: string;
  occurrence_id?: string | null;
  shift?: 'Morning' | 'Evening' | 'Anytime' | null;
  proof_photos?: number;
  proof_source?: 'camera' | 'any';
  photo_count?: number;
  locked?: boolean;
  later?: boolean;
  reopen_requested_at?: string | null;
  title: string;
  description?: string | null;
  task_type: string;
  priority: string;
  due_date?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface Hub { id: string; name: string; is_default?: boolean; active?: boolean }

export interface DayChange {
  kind: 'pause' | 'move' | 'quantity';
  start_date: string;
  end_date?: string;
  to_shift?: Shift;
  morning_litres?: number;
  evening_litres?: number;
}

/** A cow for the milking picker (GET /animals, /animals/lookup). */
export interface HerdAnimal {
  id: string;
  animal_id: string;
  animal_name?: string | null;
  primary_tag?: string | null;
  production_status?: string | null;
  health_status?: string | null;
}

export type MilkShift = 'Morning' | 'Evening' | 'Third';

export interface CorrectionFigures { litres: number; fat_percent?: number; snf_percent?: number; remarks?: string }

/** One milk entry (GET /milk-sessions). */
export interface MilkEntry {
  id: string;
  session_code?: string | null;
  animal_id?: string | null;
  group_animal_ids?: string[] | null;
  production_date: string;
  session_type: MilkShift;
  litres: string;
  fat_percent?: string | null;
  snf_percent?: string | null;
  remarks?: string | null;
  entered_by: string;
  approval_status: 'Pending' | 'Approved' | 'Rejected';
  review_notes?: string | null;
  created_at: string;
  agent_edit_count?: number;
  correction_request?: { litres: number; fat_percent?: number | null; reason: string; requested_by_name?: string; requested_at?: string } | null;
}

export const MAX_OWN_CORRECTIONS = 2;

/** GET /milk-sales/parties - a Partner / Customer / Vendor to sell milk to. */
export interface SaleParty {
  id: string;
  name: string;
  party_type: 'Partner' | 'Customer' | 'Vendor';
  default_rate_per_litre?: string | null;
  subscription_active?: boolean;
  subscription_morning_litres?: string | null;
  subscription_evening_litres?: string | null;
  subscription_rate?: string | null;
}

export interface Spill { id: string; spill_date: string; litres: string; notes?: string | null; review_status: string; created_at: string; agent_name: string }

const get = <T,>(url: string, params?: object) => apiClient.get<T>(url, { params }).then((r) => r.data);
const post = <T,>(url: string, body?: unknown) => apiClient.post<T>(url, body).then((r) => r.data);

function upload(file: File, entityType: string, entityId: string) {
  const form = new FormData();
  form.append('entity_type', entityType);
  form.append('entity_id', entityId);
  form.append('file', file);
  return apiClient.post<{ id: string }>('/attachments', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
}

export const agentApi = {
  route: () => get<RouteItem[]>('/agent/route'),
  me: () => get<AgentDetail>('/agent/me'),
  hub: () => get<HubShift[]>('/agent/hub-status'),
  history: (agentId: string) => get<Delivery[]>('/deliveries', { agent_id: agentId }),
  confirm: (body: {
    customer_id: string; sale_date: string; verification_mode: 'QRScan' | 'OfflineBypass';
    qr_token?: string; captured_latitude?: number; captured_longitude?: number; captured_at?: string;
    litres?: number; rate_per_litre?: number; // a one-off sale (no plan to read them from)
  }) => post<Delivery>('/deliveries/confirm', body),
  parties: () => get<SaleParty[]>('/milk-sales/parties'),
  /** A sale recorded without QR (or Calves / Office use) - waits for the office. */
  sale: (body: Record<string, unknown>) => post<{ id: string }>('/milk-sales', body),
  skip: (customerId: string, saleDate: string, shift: Shift) => post<Delivery>('/deliveries/skip', { customer_id: customerId, sale_date: saleDate, shift }),
  dayChange: (customerId: string, change: DayChange) => post('/subscription-day-changes', { ...change, customer_id: customerId }),

  hubs: () => get<Paginated<Hub>>('/dairy-collection-hubs', { page_size: 200, active: true }).then((p) => p.items),
  /** A Hub slip: the sale first, then its photo (a failed photo removes the sale again). */
  async hubSale(body: Record<string, unknown>, photo: File) {
    const sale = await post<{ id: string }>('/milk-sales', body);
    try {
      const att = await upload(photo, 'MilkSale', sale.id);
      await apiClient.put(`/milk-sales/${sale.id}/photo`, { photo_attachment_id: att.id });
    } catch (err) {
      await apiClient.delete(`/milk-sales/${sale.id}`).catch(() => {});
      throw err;
    }
    return sale;
  },

  // Record Milking - one cow, or a batch of cows with one combined quantity.
  herd: () => get<Paginated<HerdAnimal>>('/animals', { sort: 'id', page_size: 500 }).then((p) => p.items),
  /** Whatever was scanned: the animal's QR, its ID, or an ear-tag barcode. */
  lookupAnimal: (code: string) => get<HerdAnimal>('/animals/lookup', { code }),
  milkBetween: (from: string, to: string) => get<Paginated<MilkEntry>>('/milk-sessions', { date_from: from, date_to: to, page_size: 500 }).then((p) => p.items),
  milkOn: (day: string) => get<Paginated<MilkEntry>>('/milk-sessions', { date_from: day, date_to: day, page_size: 500 }).then((p) => p.items),
  /** The person who recorded it fixes it: twice, within 24 hours, while Pending. */
  myCorrection: (id: string, body: CorrectionFigures) => apiClient.put<MilkEntry>(`/milk-sessions/${id}/my-correction`, body).then((r) => r.data),
  /** After that (or once approved): ask the office, with the reason. */
  requestCorrection: (id: string, body: CorrectionFigures & { reason: string }) => post<MilkEntry>(`/milk-sessions/${id}/correction-request`, body),
  async milking(body: Record<string, unknown>, photo: File | null) {
    const s = await post<{ id: string }>('/milk-sessions', body);
    if (photo) {
      try {
        const att = await upload(photo, 'MilkSession', s.id);
        await apiClient.put(`/milk-sessions/${s.id}/photo`, { photo_attachment_id: att.id });
      } catch (err) {
        await apiClient.delete(`/milk-sessions/${s.id}`).catch(() => {});
        throw err;
      }
    }
    return s;
  },

  tasks: (includeDone: boolean) => get<MyTask[]>('/task-assignments/mine', { include_done: includeDone }),
  startTask: (t: MyTask) => post(t.kind === 'occurrence' && t.occurrence_id ? `/task-occurrences/${t.occurrence_id}/start` : `/task-assignments/${t.assignment_id}/start`),
  completeTask: (t: MyTask) => post(t.kind === 'occurrence' && t.occurrence_id ? `/task-occurrences/${t.occurrence_id}/complete` : `/task-assignments/${t.assignment_id}/complete`, {}),
  taskPhoto: (t: MyTask, file: File) => upload(file, 'TaskOccurrence', t.occurrence_id!),
  reopen: (t: MyTask, reason: string) => post(`/task-occurrences/${t.occurrence_id}/reopen-request`, { reason }),

  spills: () => get<Spill[]>('/spill-logs'),
  spill: (body: { spill_date: string; litres: number; notes?: string; latitude?: number; longitude?: number }, photo: File) => {
    const form = new FormData();
    form.append('spill_date', body.spill_date);
    form.append('litres', String(body.litres));
    if (body.latitude != null) form.append('latitude', String(body.latitude));
    if (body.longitude != null) form.append('longitude', String(body.longitude));
    if (body.notes) form.append('notes', body.notes);
    form.append('file', photo);
    return apiClient.post('/spill-logs', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
  },

  // The office's live view of every agent.
  overview: () => get<AgentOverview[]>('/agent/overview', { include_idle: true }),
  detail: (userId: string) => get<AgentDetail>(`/agent/overview/${userId}`),
};

/** AM before 12:00 and PM from 12:00, on the farm's clock (India time),
 * whatever time zone the phone is set to - the same rule as the server. */
export function farmShift(at = Date.now()): Shift {
  return new Date(at + 5.5 * 3600_000).getUTCHours() < 12 ? 'Morning' : 'Evening';
}

/** Today's date on the farm's clock (YYYY-MM-DD), whatever the phone's time zone. */
export function todayIso(): string {
  return new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
}

export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const x = new Date(y, m - 1, d + n);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

/** The phone's location, or null when it's refused / unavailable. */
export function getPosition(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
  });
}
